import { useRef, useState } from "react";
import { Plus, Trash2, Image as ImageIcon, X, CheckCircle2 } from "lucide-react";
import { FormField, inputClass, buttonClass } from "./shared";
import useLearningPlatform from "../../../api/learningPlatform";

const QUESTION_TYPES = [
  { value: "single_choice", label: "Eén juist antwoord" },
  { value: "multiple_choice", label: "Meerdere juiste antwoorden" },
  { value: "true_false", label: "Waar / niet waar" },
  { value: "short_answer", label: "Open vraag (kort antwoord)" },
];

const blankOption = () => ({
  id: cryptoId(),
  text: "",
  image: "",
  correct: false,
});

const blankQuestion = (type = "single_choice") => {
  const base = {
    id: cryptoId(),
    type,
    prompt: "",
    image: "",
    explanation: "",
    points: 1,
  };
  if (type === "true_false") {
    return {
      ...base,
      options: [
        { id: cryptoId(), text: "Waar", correct: true },
        { id: cryptoId(), text: "Niet waar", correct: false },
      ],
    };
  }
  if (type === "short_answer") {
    return { ...base, correct_answer: "" };
  }
  return { ...base, options: [blankOption(), blankOption()] };
};

const cryptoId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `q-${Math.random().toString(36).slice(2)}`;
};

const QuizEditor = ({ value, onChange, isDark }) => {
  const data = value || { pass_threshold: 70, shuffle: false, questions: [] };
  const update = (patch) => onChange({ ...data, ...patch });

  const setQuestion = (qid, patch) => {
    update({
      questions: data.questions.map((q) =>
        q.id === qid ? { ...q, ...patch } : q
      ),
    });
  };

  const addQuestion = () => {
    update({ questions: [...(data.questions || []), blankQuestion()] });
  };

  const removeQuestion = (qid) => {
    update({ questions: data.questions.filter((q) => q.id !== qid) });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Slagingspercentage (%)" isDark={isDark}>
          <input
            type="number"
            min="0"
            max="100"
            value={data.pass_threshold ?? 70}
            onChange={(e) =>
              update({ pass_threshold: Number(e.target.value) || 0 })
            }
            className={inputClass(isDark)}
          />
        </FormField>
        <FormField label="Willekeurige volgorde" isDark={isDark}>
          <label
            className={`flex items-center gap-2 text-[13px] mt-2 cursor-pointer ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}
          >
            <input
              type="checkbox"
              checked={!!data.shuffle}
              onChange={(e) => update({ shuffle: e.target.checked })}
            />
            Vragen in willekeurige volgorde tonen
          </label>
        </FormField>
      </div>

      <div className="space-y-3">
        {(data.questions || []).map((q, idx) => (
          <QuestionCard
            key={q.id}
            isDark={isDark}
            index={idx}
            question={q}
            onChange={(patch) => setQuestion(q.id, patch)}
            onRemove={() => removeQuestion(q.id)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addQuestion}
        className={buttonClass(isDark, "secondary")}
      >
        <Plus size={13} /> Vraag toevoegen
      </button>
    </div>
  );
};

const QuestionCard = ({ isDark, index, question, onChange, onRemove }) => {
  const { uploadMedia } = useLearningPlatform();
  const promptFileRef = useRef(null);
  const optionFileRefs = useRef({});
  const [uploadingPrompt, setUploadingPrompt] = useState(false);
  const [uploadingOption, setUploadingOption] = useState(null);

  const setType = (newType) => {
    const next = blankQuestion(newType);
    onChange({
      type: newType,
      // Try to preserve prompt / explanation
      prompt: question.prompt,
      explanation: question.explanation,
      image: question.image,
      points: question.points,
      options: next.options,
      correct_answer: next.correct_answer,
    });
  };

  const uploadPromptImage = async (file) => {
    if (!file) return;
    setUploadingPrompt(true);
    try {
      const result = await uploadMedia(file);
      if (result?.url) onChange({ image: result.url });
    } finally {
      setUploadingPrompt(false);
    }
  };

  const uploadOptionImage = async (optionId, file) => {
    if (!file) return;
    setUploadingOption(optionId);
    try {
      const result = await uploadMedia(file);
      if (result?.url) {
        onChange({
          options: question.options.map((o) =>
            o.id === optionId ? { ...o, image: result.url } : o
          ),
        });
      }
    } finally {
      setUploadingOption(null);
    }
  };

  const updateOption = (optionId, patch) => {
    onChange({
      options: question.options.map((o) =>
        o.id === optionId ? { ...o, ...patch } : o
      ),
    });
  };

  const addOption = () => {
    onChange({ options: [...(question.options || []), { id: cryptoId(), text: "", correct: false }] });
  };

  const removeOption = (optionId) => {
    onChange({ options: question.options.filter((o) => o.id !== optionId) });
  };

  const toggleCorrect = (optionId) => {
    if (question.type === "single_choice" || question.type === "true_false") {
      onChange({
        options: question.options.map((o) => ({
          ...o,
          correct: o.id === optionId,
        })),
      });
    } else {
      updateOption(optionId, {
        correct: !question.options.find((o) => o.id === optionId)?.correct,
      });
    }
  };

  return (
    <div
      className={`rounded-md border p-3 space-y-3 ${
        isDark
          ? "border-white/[0.08] bg-white/[0.02]"
          : "border-gray-200 bg-gray-50/50"
      }`}
    >
      <div className="flex items-start gap-2">
        <span
          className={`text-[11px] uppercase tracking-wider font-semibold mt-2 ${
            isDark ? "text-gray-500" : "text-gray-500"
          }`}
        >
          Vraag {index + 1}
        </span>
        <div className="flex-1" />
        <select
          value={question.type}
          onChange={(e) => setType(e.target.value)}
          className={`${inputClass(isDark)} max-w-xs`}
        >
          {QUESTION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onRemove}
          className={`p-1.5 rounded transition-colors ${
            isDark
              ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
              : "text-gray-400 hover:text-red-500 hover:bg-red-50"
          }`}
          title="Verwijder vraag"
        >
          <Trash2 size={13} />
        </button>
      </div>

      <FormField label="Vraagstelling" isDark={isDark}>
        <textarea
          rows={2}
          value={question.prompt || ""}
          onChange={(e) => onChange({ prompt: e.target.value })}
          placeholder="bv. Wat is de hoofdstad van België?"
          className={inputClass(isDark)}
        />
      </FormField>

      <div className="flex items-start gap-3">
        {question.image ? (
          <div className="relative">
            <img
              src={question.image}
              alt=""
              className="h-20 w-32 object-cover rounded border"
              style={{
                borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb",
              }}
            />
            <button
              type="button"
              onClick={() => onChange({ image: "" })}
              className="absolute -top-1.5 -right-1.5 bg-black/70 text-white rounded-full p-0.5"
              title="Verwijder afbeelding"
            >
              <X size={10} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => promptFileRef.current?.click()}
            className={`h-20 w-32 rounded border flex items-center justify-center text-[11px] flex-col gap-1 ${
              isDark
                ? "border-white/[0.08] text-gray-500 hover:bg-white/[0.04]"
                : "border-gray-200 text-gray-400 hover:bg-white"
            }`}
            disabled={uploadingPrompt}
          >
            <ImageIcon size={14} />
            {uploadingPrompt ? "Uploaden..." : "Afbeelding"}
          </button>
        )}
        <input
          ref={promptFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => uploadPromptImage(e.target.files?.[0])}
        />
        <FormField label="Punten" isDark={isDark}>
          <input
            type="number"
            min="0"
            max="100"
            value={question.points ?? 1}
            onChange={(e) => onChange({ points: Number(e.target.value) || 0 })}
            className={`${inputClass(isDark)} max-w-[100px]`}
          />
        </FormField>
      </div>

      {question.type === "short_answer" ? (
        <FormField label="Verwacht antwoord" hint="(niet hoofdletter/spatie gevoelig)" isDark={isDark}>
          <input
            type="text"
            value={question.correct_answer || ""}
            onChange={(e) => onChange({ correct_answer: e.target.value })}
            className={inputClass(isDark)}
            placeholder="bv. Brussel"
          />
        </FormField>
      ) : (
        <div className="space-y-2">
          <span
            className={`text-[12px] font-medium ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Antwoordmogelijkheden
          </span>
          <div className="space-y-1.5">
            {(question.options || []).map((option) => (
              <div
                key={option.id}
                className={`flex items-center gap-2 rounded-md border p-2 ${
                  option.correct
                    ? isDark
                      ? "border-emerald-500/30 bg-emerald-500/[0.04]"
                      : "border-emerald-200 bg-emerald-50/50"
                    : isDark
                      ? "border-white/[0.08] bg-white/[0.02]"
                      : "border-gray-200 bg-white"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleCorrect(option.id)}
                  title="Markeer als juist"
                  className={`p-1 rounded transition-colors ${
                    option.correct
                      ? isDark
                        ? "text-emerald-400"
                        : "text-emerald-600"
                      : isDark
                        ? "text-gray-500 hover:text-emerald-400"
                        : "text-gray-400 hover:text-emerald-600"
                  }`}
                >
                  <CheckCircle2 size={14} />
                </button>
                <input
                  type="text"
                  value={option.text || ""}
                  onChange={(e) =>
                    updateOption(option.id, { text: e.target.value })
                  }
                  placeholder="Antwoord..."
                  className={inputClass(isDark)}
                />
                {option.image ? (
                  <div className="relative">
                    <img
                      src={option.image}
                      alt=""
                      className="h-8 w-8 object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={() => updateOption(option.id, { image: "" })}
                      className="absolute -top-1 -right-1 bg-black/70 text-white rounded-full p-0.5"
                    >
                      <X size={8} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => optionFileRefs.current[option.id]?.click()}
                    disabled={uploadingOption === option.id}
                    title="Voeg afbeelding toe"
                    className={`p-1.5 rounded ${
                      isDark
                        ? "text-gray-500 hover:text-gray-200 hover:bg-white/[0.06]"
                        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <ImageIcon size={12} />
                  </button>
                )}
                <input
                  ref={(el) => (optionFileRefs.current[option.id] = el)}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) =>
                    uploadOptionImage(option.id, e.target.files?.[0])
                  }
                />
                {question.type !== "true_false" && (
                  <button
                    type="button"
                    onClick={() => removeOption(option.id)}
                    className={`p-1.5 rounded ${
                      isDark
                        ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                        : "text-gray-400 hover:text-red-500 hover:bg-red-50"
                    }`}
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {question.type !== "true_false" && (
            <button
              type="button"
              onClick={addOption}
              className={buttonClass(isDark, "ghost")}
            >
              <Plus size={11} /> Antwoord
            </button>
          )}
        </div>
      )}

      <FormField label="Uitleg (toon na inzending)" isDark={isDark}>
        <textarea
          rows={2}
          value={question.explanation || ""}
          onChange={(e) => onChange({ explanation: e.target.value })}
          placeholder="Optionele uitleg voor de student"
          className={inputClass(isDark)}
        />
      </FormField>
    </div>
  );
};

export default QuizEditor;
